import React from "react";
//#region types
import { ParsableDate, SimpleTimePickerProps } from "models/propTypes/datepickerPropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import DatePickerLocalization from "./datePickerLocalization";
import Icon from "../icon";
import { InputAdornment, TextField, ClickAwayListener } from "@mui/material";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { CSSHelper } from "utils/helpers";

const SimpleTimePicker: React.FC<SimpleTimePickerProps> = (props) => {
  const { success, displayType, overridePlaceholder, value, ...timePickerProps } = props;
  const [shrinkLabel, setShrinkLabel] = React.useState<boolean>(!!props.value);

  const [pickerOpened, setPickerOpened] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!props.value && shrinkLabel) setShrinkLabel(false);
    else if (props.value && !shrinkLabel) setShrinkLabel(true);
  }, [props.value]);

  const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.InputProps && props.InputProps.onFocus && props.InputProps.onFocus(event);
    setShrinkLabel(true);
    setPickerOpened(true);
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.InputProps && props.InputProps.onBlur && props.InputProps.onBlur(event);
    props.onBlur && props.onBlur(event);
    !props.value && setShrinkLabel(false);
  };

  const onAccept = () => {
    setShrinkLabel(true);
  };

  const onPickerOpened = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setPickerOpened(true);
  };

  const onPickerClosed = () => {
    setPickerOpened(false);
  };

  const onChange = (date: Date | null) => {
    const time = date ? `${date.getHours()}:${date.getMinutes()}` : undefined;
    props.onChange(time ?? undefined);
  };

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    inputRef?.current?.focus();
  };

  const convertToDateString = (value?: ParsableDate) => {
    if (typeof value !== "string") return undefined;
    const split = value?.split(":");

    return split ? new Date(new Date().setHours(Number(split[0]) ?? 0, Number(split[1]) ?? 0)) : undefined;
  };

  return (
    <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={onPickerClosed}>
      <span
        role="presentation"
        className={clsx(
          cssExports.iconInputContainer,
          cssExports.datePickerInput,
          pickerOpened ? cssExports.pickerOpened : ""
        )}
      >
        <DatePickerLocalization>
          <DesktopTimePicker
            {...timePickerProps}
            views={props.views ?? ["hours", "minutes"]}
            value={convertToDateString(props.value) ?? null}
            mask="__:__"
            ampm={false}
            onChange={onChange}
            showToolbar={false}
            onAccept={onAccept}
            components={{
              OpenPickerIcon: () => (
                <span onClick={onPickerOpened}>
                  {props.keyboardIcon ?? (
                    <Icon icon={IconDefinitionConstants.WClock} className={cssExports.inputIcon} />
                  )}
                </span>
              ),
            }}
            inputRef={inputRef}
            InputAdornmentProps={{
              position: "start",
              className: cssExports.inputAdornedStart,
            }}
            InputProps={{
              classes: {
                root: clsx(
                  cssExports.inputRoot,
                  cssExports.iconInput,
                  shrinkLabel ? cssExports.shrinkedLabelInput : ""
                ),
                input: cssExports.inputInput,
                focused: cssExports.focusedInput,
                error: cssExports.errorInput,
                disabled: cssExports.disabledInput,
              },
              onBlur: onBlur,
              onFocus: onFocus,
              autoComplete: "off",
              endAdornment: (
                <>
                  {(success as boolean) ? (
                    <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.success)}>
                      <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
                    </InputAdornment>
                  ) : (
                    <></>
                  )}
                  {props.disabled ? (
                    <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                      <Icon icon={IconDefinitionConstants.WLock} className={cssExports.inputIcon} />
                    </InputAdornment>
                  ) : (
                    <></>
                  )}
                </>
              ),
            }}
            PopperProps={{
              className: clsx(cssExports.datePickerPopover, CSSHelper.getClassByDisplayType(displayType)),
              open: pickerOpened,
            }}
            OpenPickerButtonProps={{
              "aria-label": "change date",
            }}
            onOpen={onPickerOpened}
            onClose={onPickerClosed}
            renderInput={(elements) => (
              <TextField
                {...elements}
                id="time"
                name={props.name}
                label={shrinkLabel ? props.label : overridePlaceholder ?? props.label}
                helperText=""
                type="time"
                InputLabelProps={{
                  shrink: shrinkLabel,
                  classes: {
                    root: clsx(cssExports.inputLabel, cssExports.iconInputLabel),
                    error: cssExports.errorLabel,
                    shrink: cssExports.shrinkedLabel,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    root: cssExports.inputHelperText,
                    error: cssExports.errorHelperText,
                  },
                }}
                onClick={onClick}
                error={props.error}
              />
            )}
          />
        </DatePickerLocalization>
      </span>
    </ClickAwayListener>
  );
};

export default SimpleTimePicker;
